import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import Header from "../../components/header";
import Loader from "../../components/loader";
import UIButton from "../../components/ui-button";
import Badge from "../../components/badge";
import Card from "../../components/card";
import Panel from "../../components/panel";
import StepContainer from "../../components/step-container";
import PlanCard from "../../components/plan-card";
import LossItem from "../../components/loss-item";
import OfferBox from "../../components/offer-box";
import TextField from "../../components/form-text-field";
import { useAvailableDowngradePlans } from "../../hooks/useAvailableDowngradePlans";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import FunnelStepTypes from "../funnel-step/types";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PathHelper from "../../utils/path-helper";
import { readCookie, getPortalCD } from "../../utils/helpers";
import {
  InvitationStatus,
  getGuestUserStatus,
  getInvitationStatusColor,
} from "../../utils/utility-functions";
import { useTranslation } from "react-i18next";
import trashIcon from "../../assets/pre-cancel/trash-icon.svg";
import checkIcon from "../../assets/pre-cancel/check-icon.svg";
import infoIcon from "../../assets/pre-cancel/info-icon.svg";
import FooterV2 from "../../components/footer-v2";

const EXCLUDED_SKU_SUB_CATEGORY = "DOWN0195";

const INVITE_FIELDS = ["firstName", "lastName", "email"];

const BENEFITS = [
  { title: "pre_cancel.benefit_1_title", note: "pre_cancel.benefit_1_note" },
  { title: "pre_cancel.benefit_2_title", note: "pre_cancel.benefit_2_note" },
  { title: "pre_cancel.benefit_3_title", note: "pre_cancel.benefit_3_note" },
];

const LOSSES = ["pre_cancel.loss_3", "pre_cancel.loss_4", "pre_cancel.loss_5"];

const INVITE_STATUS = {
  SUCCESS: "Success",
  USER_ALREADY_EXISTS: "UserAlreadyExsists",
};

/* ================================================================
   REUSABLE — InviteForm
   ================================================================ */

function InviteForm({
  maxUserCount,
  availableSlots,
  rows,
  onFieldChange,
  onAddRow,
  onDeleteRow,
  rowStatuses = [],
}) {
  const { t } = useTranslation();
  const placeholders = {
    firstName: t("pre_cancel.placeholder_first_name"),
    lastName: t("pre_cancel.placeholder_last_name"),
    email: t("pre_cancel.placeholder_email"),
  };

  const visibleRows = Math.min(rows.length, availableSlots);

  return (
    <>
      <div className="flex flex-col gap-4">
        {Array.from({ length: visibleRows }).map((_, rowIdx) => {
          const rowStatus = rowStatuses[rowIdx];
          return (
            <div key={rowIdx} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-text-dark">
                  {t("pre_cancel.guest_user", { count: rowIdx + 1 })}
                </span>
                <div className="flex items-center gap-2">
                  {rowStatus?.loading && (
                    <div
                      className="w-4 h-4 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin"
                      title={t("pre_cancel.sending")}
                    />
                  )}
                  {!rowStatus?.loading &&
                    rowStatus?.result === INVITE_STATUS.SUCCESS && (
                      <img
                        src={checkIcon}
                        alt={t("pre_cancel.invitations_sent")}
                        title={t("pre_cancel.invitations_sent")}
                        className="w-5 h-5"
                      />
                    )}
                  {!rowStatus?.loading &&
                    rowStatus?.result === INVITE_STATUS.USER_ALREADY_EXISTS && (
                      <img
                        src={infoIcon}
                        alt={t("pre_cancel.user_already_exists")}
                        title={t("pre_cancel.user_already_exists")}
                        className="w-5 h-5"
                      />
                    )}
                  {rowIdx > 0 && onDeleteRow && (
                    <UIButton
                      variant="link"
                      onClick={() => onDeleteRow(rowIdx)}
                      className="p-1 -mr-1 rounded"
                    >
                      <img
                        src={trashIcon}
                        alt={t("pre_cancel.remove_row")}
                        className="w-4 h-4"
                      />
                    </UIButton>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1.5fr] gap-2">
                <TextField
                  type="text"
                  name="firstName"
                  placeholder={placeholders.firstName}
                  value={rows[rowIdx]?.firstName || ""}
                  onChange={(e) =>
                    onFieldChange(rowIdx, "firstName", e.target.value)
                  }
                />
                <TextField
                  type="text"
                  name="lastName"
                  placeholder={placeholders.lastName}
                  value={rows[rowIdx]?.lastName || ""}
                  onChange={(e) =>
                    onFieldChange(rowIdx, "lastName", e.target.value)
                  }
                />
                <div className="col-span-2 sm:col-span-1">
                  <TextField
                    type="email"
                    name="email"
                    placeholder={placeholders.email}
                    value={rows[rowIdx]?.email || ""}
                    onChange={(e) =>
                      onFieldChange(rowIdx, "email", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {visibleRows < availableSlots && (
        <div className="flex items-center gap-[18px] flex-wrap">
          <UIButton
            variant="link"
            onClick={onAddRow}
            className="!font-bold !text-[15px]"
          >
            + {t("pre_cancel.add_another")}
          </UIButton>
          <span className="text-sm text-text-light">
            {maxUserCount} {t("pre_cancel.seats_included")}
          </span>
        </div>
      )}
    </>
  );
}

/* ================================================================
   STEP 1 — Benefits
   ================================================================ */

function BenefitsStep({
  onConfirmCancel,
  planData,
  selectedPlan,
  onSelectPlan,
  onConfirm,
  loading,
  updatingPlan,
  hideCurrentPlan,
  currentCurrencySymbol,
  currentRecurringPrice,
  hideKeepForLess,
}) {
  const { t } = useTranslation();
  const selected = planData[selectedPlan - 1];
  const featuredPlanIndex = Math.floor(planData.length / 2);

  return (
    <StepContainer>
      <div className="flex flex-col gap-3">
        <Badge color="green">{t("pre_cancel.eyebrow_before")}</Badge>
        <div className="font-bold text-[clamp(25px,5.2vw,32px)] leading-[1.13] tracking-[-0.01em] max-w-[24ch]">
          {t("pre_cancel.sure_cancel")}
        </div>
        <div
          className="text-base leading-[1.6] text-text-muted max-w-[46ch]"
          dangerouslySetInnerHTML={{ __html: t("pre_cancel.keep_access") }}
        />
      </div>

      <div className="flex flex-col gap-3">
        {BENEFITS.map((b) => (
          <div
            key={b.title}
            className="flex gap-4 items-start bg-white border border-border-default rounded-[18px] p-5 pr-[22px]"
          >
            <span className="flex-none w-[26px] h-[26px] rounded-full bg-status-success-bg text-status-success-text flex items-center justify-center text-sm font-bold">
              ✓
            </span>
            <div className="flex flex-col gap-1">
              <div className="font-bold text-[17px] leading-[1.3]">
                {t(b.title)}
              </div>
              <div className="text-[15px] leading-[1.55] text-text-body">
                {t(b.note)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {!hideKeepForLess && (
        <>
          <div className="flex flex-col gap-3">
            <div className="font-bold text-[clamp(20px,4.4vw,24px)] leading-[1.2]">
              {t("pre_cancel.keep_account_less")}
            </div>
            <div className="text-base leading-[1.65] text-text-body max-w-[52ch]">
              {t("pre_cancel.every_option_keeps")}{" "}
              <strong className="text-text-dark">
                {t("pre_cancel.full_access")}
              </strong>
              , {t("pre_cancel.plus_sharing")}
            </div>
          </div>

          <Loader isLoading={updatingPlan} />

          <div
            className={`grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3 ${updatingPlan ? "opacity-50 pointer-events-none" : ""}`}
          >
            {!loading &&
              planData.map((plan, index) => {
                const savingsPercent = plan?.breakDown;
                return (
                  <PlanCard
                    key={plan.id}
                    price={plan.unitPrice}
                    currencySymbol={plan.currencySymbol}
                    periodLabel={t("pre_cancel.per_month")}
                    seatsLabel={plan.maxSubUserCount}
                    isSelected={selectedPlan === index + 1}
                    isFeatured={index === featuredPlanIndex}
                    badge={
                      index === featuredPlanIndex
                        ? t("pre_cancel.most_popular")
                        : selectedPlan === index + 1
                          ? t("pre_cancel.selected")
                          : ""
                    }
                    savings={savingsPercent}
                    onClick={() => onSelectPlan(index + 1)}
                  />
                );
              })}
          </div>

          <div className="flex flex-col gap-2.5 items-stretch">
            <UIButton
              variant="block"
              onClick={onConfirm}
              disabled={updatingPlan}
            >
              {updatingPlan
                ? t("pre_cancel.updating")
                : (() => {
                    if (!selected) return t("pre_cancel.confirm_plan");
                    const savings = selected.breakDown;
                    return t("pre_cancel.confirm_plan_savings", {
                      currencySymbol: selected?.currencySymbol,
                      price: selected?.recurringPrice,
                      savings,
                    });
                  })()}
            </UIButton>
            <div className="text-center text-sm text-text-body">
              {t("pre_cancel.that_only")} {selected?.currencySymbol}
              {selected?.savingsText}
              {t("pre_cancel.per_user_note")}
            </div>
          </div>
        </>
      )}

      <div
        className={`flex items-center justify-center md:justify-between gap-4 flex-wrap pt-2 border-t border-border-subtle max-sm:flex-col max-sm:justify-center max-sm:items-center ${
          hideKeepForLess ? "md:justify-center" : ""
        }`}
      >
        {!hideCurrentPlan && (
          <span className="text-sm text-text-light">
            {t("pre_cancel.currently", {
              currencySymbol: currentCurrencySymbol,
              price: currentRecurringPrice,
            })}
          </span>
        )}
        <UIButton variant="link" onClick={onConfirmCancel}>
          {t("pre_cancel.yes_cancel_delete")}
        </UIButton>
      </div>
    </StepContainer>
  );
}

/* ================================================================
   STEP 3 — Invite (post-downgrade confirmation)
   ================================================================ */

function InviteStep({
  maxUserCount,
  existingUserCount,
  activeGuestUsers,
  currencySymbol,
  recurringPrice,
  rows,
  onFieldChange,
  onAddRow,
  onDeleteRow,
  rowStatuses,
  onSubmit,
  onDone,
  submitting,
  breakDown,
}) {
  const { t } = useTranslation();

  // How many new users can still be invited on the new plan
  const availableSlots = Math.max(
    0,
    (maxUserCount ?? 0) - (existingUserCount ?? 0),
  );
  const slotsExhausted = availableSlots === 0 && existingUserCount > 0;

  const visibleRows = Math.min(rows.length, availableSlots);
  const hasEmptyFields = Array.from({ length: visibleRows }).some((_, i) =>
    INVITE_FIELDS.some((field) => !(rows[i]?.[field] || "").trim()),
  );

  return (
    <StepContainer>
      <div className="flex flex-col gap-3">
        <Badge color="purple">{t("pre_cancel.plan_updated")}</Badge>
        <div className="font-bold text-[clamp(25px,5.2vw,32px)] leading-[1.14] tracking-[-0.01em]">
          {t("pre_cancel.nicely_done", { savingsText: breakDown })}
        </div>
        <div className="text-base leading-[1.65] text-text-body max-w-[50ch]">
          {t("pre_cancel.youre_now_on", {
            price:
              currencySymbol && recurringPrice
                ? `${currencySymbol}${recurringPrice}`
                : "",
          })}
        </div>
      </div>

      <Panel>
        <div className="flex flex-col gap-2">
          <div className=" font-bold text-xl">
            {t("pre_cancel.your_plan_includes", { maxUserCount })}
          </div>
          <div className="text-[15px] leading-[1.6] text-text-body max-w-[50ch]">
            {t("pre_cancel.family_friends")}
          </div>
        </div>

        {activeGuestUsers.length > 0 && (
          <div className="text-sm text-text-body">
            {t("pre_cancel.active_guest_users_count", {
              count: activeGuestUsers.length,
            })}
          </div>
        )}

        {activeGuestUsers.length > 0 && (
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="text-left font-bold py-2 pr-4 text-text-dark">
                    {t("pre_cancel.invited_email")}
                  </th>
                  <th className="text-left font-bold py-2 text-text-dark">
                    {t("pre_cancel.invited_status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {activeGuestUsers.map((user, i) => {
                  const statusText = getGuestUserStatus(
                    user.invitationStatus,
                    t,
                  );
                  const badgeColor = getInvitationStatusColor(statusText, t);
                  return (
                    <tr
                      key={i}
                      className="border-b border-border-subtle last:border-0"
                    >
                      <td className="py-3 pr-4 text-text-body break-all">
                        {user.emailAddress}
                      </td>
                      <td className="py-3">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}
                        >
                          {statusText}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {slotsExhausted ? (
          <div className="flex flex-col items-center justify-center text-center py-10">
            <div className="text-sm text-text-body">
              {t("pre_cancel.no_slots_available")}
            </div>
            <UIButton variant="ghost" onClick={onDone} className="mt-4">
              {t("pre_cancel.go_to_dashboard")}
            </UIButton>
          </div>
        ) : (
          <>
            <InviteForm
              maxUserCount={maxUserCount}
              availableSlots={availableSlots}
              rows={rows}
              onFieldChange={onFieldChange}
              onAddRow={onAddRow}
              onDeleteRow={onDeleteRow}
              rowStatuses={rowStatuses}
            />

            <div className="flex items-center justify-center md:justify-start gap-[18px] flex-wrap pt-1">
              <UIButton
                variant="secondary"
                onClick={onSubmit}
                disabled={submitting || hasEmptyFields}
              >
                {submitting
                  ? t("pre_cancel.sending")
                  : t("pre_cancel.send_invitations")}
              </UIButton>
              <UIButton variant="ghost" onClick={onDone} disabled={submitting}>
                {t("pre_cancel.do_later")}
              </UIButton>
            </div>
          </>
        )}
      </Panel>
    </StepContainer>
  );
}

/* ================================================================
   STEP 4 — Cancellation Warning
   ================================================================ */

function CancelWarningStep({
  cheapestPlan,
  onSeeOptions,
  onKeepBest,
  onConfirmCancel,
  cancelling,
  hideDowngradeOptions,
  totalAssessmentAttempts,
  completedAchievements,
}) {
  const { t } = useTranslation();

  return (
    <StepContainer>
      <div className="flex flex-col gap-2.5">
        <div className="font-bold text-[clamp(24px,5vw,30px)] leading-[1.15]">
          {t("pre_cancel.cancelling_removes")}
        </div>
        <div className="text-base leading-[1.6] text-text-body max-w-[50ch]">
          {t("pre_cancel.read_once")}
        </div>
      </div>

      <Panel className="!p-[22px_24px] !gap-3">
        <LossItem key="pre_cancel.loss_1">
          {t("pre_cancel.loss_1", { count: totalAssessmentAttempts ?? 0 })}
        </LossItem>
        <LossItem key="pre_cancel.loss_2">
          {t("pre_cancel.loss_2", { count: completedAchievements ?? 0 })}
        </LossItem>
        {LOSSES.map((key) => (
          <LossItem key={key}>{t(key)}</LossItem>
        ))}
      </Panel>

      {!hideDowngradeOptions && (
        <OfferBox
          text={t("pre_cancel.keep_every")}
          price={
            cheapestPlan &&
            t("pre_cancel.keep_every_price", {
              currencySymbol: cheapestPlan.currencySymbol,
              price: cheapestPlan.unitPrice,
              savings: cheapestPlan.breakDown,
            })
          }
          actionLabel={t("pre_cancel.keep_my_account")}
          onAction={onKeepBest}
        />
      )}

      <div
        className={`flex items-center gap-5 flex-wrap max-sm:flex-col max-sm:items-center ${hideDowngradeOptions ? "justify-center" : ""}`}
      >
        {!hideDowngradeOptions && (
          <UIButton
            variant="outline"
            onClick={onSeeOptions}
            disabled={cancelling}
            className="!border-[1.5px] !border-border-default !border-solid"
          >
            {t("pre_cancel.see_other_plans")}
          </UIButton>
        )}
        <UIButton
          variant="link"
          onClick={onConfirmCancel}
          disabled={cancelling}
          className={cancelling ? "opacity-60 cursor-not-allowed" : ""}
        >
          {cancelling
            ? t("pre_cancel.cancelling")
            : t("pre_cancel.yes_cancel_delete")}
        </UIButton>
      </div>
    </StepContainer>
  );
}

/* ================================================================
   STEP 5 — Cancelled + 24h countdown
   ================================================================ */

/* ================================================================
   ROOT — PreCancel
   ================================================================ */

function PreCancel({
  getPlansConnect,
  funnelStep,
  updateUserPlanConnect,
  accountCancellationConnect,
  getOrderDataByUserIdConnect,
  getBillingConnect,
  addGuestUsersBulkConnect,
  getGuestUserConnect,
}) {
  const { locale } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLocale = locale ?? LOCALE.EN;
  const {
    countryCD,
    currencyCD,
    userGUID,
    email,
    isAuth,
    totalAssessmentAttempts,
    completedAchievements,
  } = funnelStep || {};
  const loginPath = PathHelper(APP_NAVIGATION.SINI, currentLocale);

  const dispatch = useDispatch();

  const pushBillingDetailsLocal = (payload) => ({
    type: FunnelStepTypes.SET_BILLING_DETAILS,
    ...payload,
  });

  useEffect(() => {
    if (!isAuth) {
      navigate(loginPath.next);
    }
  }, [isAuth]);

  const requestedStep = Math.min(
    Math.max(parseInt(searchParams.get("step"), 10) || 1, 1),
    5,
  );
  // Step 2 (downgrade options) was merged into step 1.
  const initialStep = requestedStep === 2 ? 1 : requestedStep;
  const fromCancelled = searchParams.get("fromCancelled") === "true";
  const [step, setStepState] = useState(initialStep);
  const [planData, setPlanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [inviteRows, setInviteRows] = useState([
    { firstName: "", lastName: "", email: "" },
  ]);
  const [orderData, setOrderData] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [billingDetailsLoading, setBillingDetailsLoading] = useState(
    Boolean(userGUID),
  );
  const [guestUsers, setGuestUsers] = useState([]);
  const [pageLoadComplete, setPageLoadComplete] = useState(false);

  const activeGuestUsers = guestUsers.filter(
    (u) =>
      u.invitationStatus === InvitationStatus.Sent ||
      u.invitationStatus === InvitationStatus.Accepted,
  );
  const existingUserCount = activeGuestUsers.length;

  const setStep = (n) => {
    setStepState(n);
    setSearchParams({ step: n });
  };

  const cancelPlanFeedbackPath = PathHelper(
    APP_NAVIGATION.CANCEL_PLAN_FEEDBACK,
    currentLocale,
  );

  const dashboardPath = PathHelper(APP_NAVIGATION.DASHBOARD_B, currentLocale);

  useEffect(() => {
    if (orderData && orderData?.transactionDetailId) {
      const data = {
        skuGuid: orderData?.skuId,
        transId: orderData?.transactionDetailId,
        locale: currentLocale,
      };
      setBillingDetailsLoading(true);
      getBillingConnect(data)
        .then((res) => {
          setBillingDetails(res);
        })
        .finally(() => {
          setBillingDetailsLoading(false);
        });
    }
  }, [orderData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (userGUID) {
      // Keep the loader on while the order request and the billing request
      // that follows it are in flight, so it does not blink off in between.
      let hasTransaction = false;
      setBillingDetailsLoading(true);
      getOrderDataByUserIdConnect(userGUID)
        .then((res) => {
          if (res && Object.keys(res).length > 0) {
            setOrderData(res);
            hasTransaction = Boolean(res.transactionDetailId);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch order data:", err);
        })
        .finally(() => {
          if (!hasTransaction) {
            setBillingDetailsLoading(false);
          }
        });

      getGuestUserConnect(userGUID)
        .then((res) => {
          if (Array.isArray(res)) {
            setGuestUsers(res);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch guest users:", err);
        });
    }
  }, [userGUID, getOrderDataByUserIdConnect, getGuestUserConnect]);

  useEffect(() => {
    setLoading(true);
    getPlansConnect({
      planType: "_dunning",
      currencyCD,
      countryCD,
      device: "null",
      locale:
        currentLocale === LOCALE.EN ||
        currentLocale === LOCALE.ES ||
        currentLocale === LOCALE.JA ||
        currentLocale === LOCALE.FR ||
        currentLocale === LOCALE.IT
          ? currentLocale
          : LOCALE.EN,
    })
      .then((result) => {
        const filteredPlans = result.filter(
          (plan) => plan.skuSubCategory !== EXCLUDED_SKU_SUB_CATEGORY,
        );
        const topPlans = [...filteredPlans].sort(
          (a, b) => a.unitPrice - b.unitPrice,
        );
        setPlanData(topPlans);
      })
      .catch((error) => {
        console.error("Failed to fetch plans:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getPlansConnect, currencyCD, countryCD, currentLocale]);

  // The full screen loader belongs to the first load only. Latching it here
  // stops any later re-fetch from switching the loader back on, which is what
  // made it show up twice.
  useEffect(() => {
    if (!loading && !billingDetailsLoading) {
      setPageLoadComplete(true);
    }
  }, [loading, billingDetailsLoading]);

  const {
    availablePlanData,
    selectedPlan,
    setSelectedPlan,
    cheapestPlan,
    noPlansAvailable,
  } = useAvailableDowngradePlans({
    planData,
    billingDetails,
    step,
    setStep,
    loading,
  });

  const activeApiPlan = availablePlanData[selectedPlan - 1] || null;

  const downgradePlansLoading = loading || billingDetailsLoading;

  const oneClickChoosePlan = (plan) => {
    const payload = {
      skuGuid: plan.id,
      price: plan.unitPrice,
      userGuid: userGUID,
      PaymentPortal: "INSIGHTFUL",
      Email: email,
      subscriptionId: readCookie("subscriptionId") || "",
      planId: plan.chargebeePlanId,
      isEndOfTerm: false,
      isIVRSource: false,
      locale: currentLocale,
    };
    setUpdatingPlan(true);
    updateUserPlanConnect(payload)
      .then(() => {
        toast.success(t("pre_cancel.plan_updated_success"));
        dispatch({
          type: FunnelStepTypes.SET_SELECTED_PLAN,
          selectedPlan: { ...plan, planKey: billingDetails?.planKey ?? "" },
        });
        dispatch(
          pushBillingDetailsLocal({
            nextBillingDate: billingDetails?.nextBillingDate ?? "",
          }),
        );
        getOrderDataByUserIdConnect(userGUID)
          .then((res) => {
            if (res && Object.keys(res).length > 0) {
              setOrderData(res);
            }
          })
          .catch((err) => {
            console.error("Failed to fetch order data:", err);
          });
        setStep(3);
      })
      .catch((error) => {
        console.error("Failed to update plan:", error);
      })
      .finally(() => {
        setUpdatingPlan(false);
      });
  };

  const handleKeepBest = () => {
    setSelectedPlan(1);
    setStep(1);
  };
  const handleConfirmCancel = async () => {
    const data = {
      isEndOfTerm: true,
      comment: "USER:CancelAccount",
      userGuid: userGUID,
      locale: currentLocale,
    };
    setCancelling(true);
    try {
      const response = await accountCancellationConnect(data);
      if (!response) {
        toast.error("settings.account_cancle_fail_msg");
        return;
      }
      dispatch({
        type: FunnelStepTypes.SET_SELECTED_PLAN,
        selectedPlan: {
          planKey: billingDetails?.planKey ?? "",
          unitPrice: billingDetails?.unitPrice ?? "",
          recurringPrice: billingDetails?.recurringPrice ?? "",
          currencySymbol: billingDetails?.currencySymbol ?? "",
          skuSubCategory: billingDetails?.skuSubCategory ?? "",
        },
      });
      dispatch(
        pushBillingDetailsLocal({
          nextBillingDate: billingDetails?.nextBillingDate ?? "",
        }),
      );
      navigate(cancelPlanFeedbackPath.next, { state: { cancelled: true } });
    } catch (err) {
      console.error("Cancellation failed:", err);
      toast.error("settings.account_cancle_fail_msg");
    } finally {
      setCancelling(false);
    }
  };

  const handleConfirmPlan = () => {
    if (activeApiPlan) oneClickChoosePlan(activeApiPlan);
  };
  const handleDone = () => {
    navigate(dashboardPath.next);
  };

  const handleFieldChange = (rowIdx, field, value) => {
    setInviteRows((prev) => {
      const updated = [...prev];
      updated[rowIdx] = { ...updated[rowIdx], [field]: value };
      return updated;
    });
  };

  const handleAddInviteRow = () => {
    const availableSlots = Math.max(
      0,
      (billingDetails?.maxSubUserCount ?? 0) - existingUserCount,
    );
    setInviteRows((prev) => {
      if (prev.length >= availableSlots) return prev;
      return [...prev, { firstName: "", lastName: "", email: "" }];
    });
  };

  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteRowStatuses, setInviteRowStatuses] = useState([]);

  const handleDeleteRow = (rowIdx) => {
    setInviteRows((prev) => prev.filter((_, i) => i !== rowIdx));
    setInviteRowStatuses((prev) => prev.filter((_, i) => i !== rowIdx));
  };

  const handleInviteSubmit = async () => {
    const filledRowsWithIndex = inviteRows
      .map((r, idx) => ({ ...r, originalIdx: idx }))
      .filter((r) => r.firstName || r.lastName || r.email);

    if (filledRowsWithIndex.length === 0) return;

    setInviteRowStatuses(
      inviteRows.map((_, i) => ({
        loading: filledRowsWithIndex.some((r) => r.originalIdx === i),
        result: null,
      })),
    );

    const payload = filledRowsWithIndex.map((r) => ({
      guestUser: {
        firstName: r.firstName,
        lastName: r.lastName,
        emailAddress: r.email,
        invitationStatus: 0,
      },
      userGuid: userGUID,
      locale: currentLocale,
      portalCD: getPortalCD(currentLocale),
    }));

    setInviteLoading(true);
    try {
      const response = await addGuestUsersBulkConnect(payload);
      if (response) {
        setInviteRowStatuses(
          inviteRows.map((_, i) => {
            const responseIdx = filledRowsWithIndex.findIndex(
              (r) => r.originalIdx === i,
            );
            return {
              loading: false,
              result: responseIdx !== -1 ? response[responseIdx] : null,
            };
          }),
        );

        const successCount = response.filter(
          (r) => r === INVITE_STATUS.SUCCESS,
        ).length;
        const alreadyExistsCount = response.filter(
          (r) => r === INVITE_STATUS.USER_ALREADY_EXISTS,
        ).length;

        if (successCount === response.length) {
          toast.success(t("pre_cancel.invitations_sent"));
          setInviteRows([{ firstName: "", lastName: "", email: "" }]);
          setInviteRowStatuses([]);
          navigate(dashboardPath.next);
        } else if (successCount > 0) {
          toast.warning(
            alreadyExistsCount > 0
              ? t("pre_cancel.some_invitations_already_exist")
              : t("pre_cancel.some_invitations_failed"),
          );
          setInviteRows([{ firstName: "", lastName: "", email: "" }]);
          setInviteRowStatuses([]);
          navigate(dashboardPath.next);
        } else if (alreadyExistsCount === response.length) {
          toast.warning(t("pre_cancel.user_already_exists"));
        } else {
          toast.error(t("pre_cancel.invitations_failed"));
        }
      }
    } catch (err) {
      console.error("Failed to send invitations:", err);
      toast.error(t("pre_cancel.invitations_failed"));
      setInviteRowStatuses((prev) =>
        prev.map(() => ({ loading: false, result: null })),
      );
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <>
      <Loader
        isLoading={(!pageLoadComplete && downgradePlansLoading) || cancelling}
      />
      <Header />
      <div className="flex justify-center bg-surface-card min-h-[calc(100vh-160px)] p-4 sm:p-0">
        <Card>
          {step === 1 && (
            <BenefitsStep
              onConfirmCancel={handleConfirmCancel}
              planData={availablePlanData}
              selectedPlan={selectedPlan}
              onSelectPlan={setSelectedPlan}
              onConfirm={handleConfirmPlan}
              loading={downgradePlansLoading}
              updatingPlan={updatingPlan}
              hideCurrentPlan={fromCancelled}
              currentCurrencySymbol={billingDetails?.currencySymbol}
              currentRecurringPrice={billingDetails?.recurringPrice}
              hideKeepForLess={noPlansAvailable}
            />
          )}
          {step === 3 && (
            <InviteStep
              maxUserCount={billingDetails?.maxSubUserCount}
              existingUserCount={existingUserCount}
              activeGuestUsers={activeGuestUsers}
              currencySymbol={billingDetails?.currencySymbol}
              recurringPrice={billingDetails?.recurringPrice}
              breakDown={billingDetails?.breakDown}
              rows={inviteRows}
              onFieldChange={handleFieldChange}
              onAddRow={handleAddInviteRow}
              onDeleteRow={handleDeleteRow}
              rowStatuses={inviteRowStatuses}
              onSubmit={handleInviteSubmit}
              onDone={handleDone}
              submitting={inviteLoading}
            />
          )}
          {step === 4 && (
            <CancelWarningStep
              cheapestPlan={cheapestPlan}
              onSeeOptions={() => setStep(1)}
              onKeepBest={handleKeepBest}
              onConfirmCancel={handleConfirmCancel}
              cancelling={cancelling}
              hideDowngradeOptions={noPlansAvailable}
              completedAchievements={completedAchievements}
              totalAssessmentAttempts={totalAssessmentAttempts}
            />
          )}
        </Card>
      </div>
      <FooterV2 />
    </>
  );
}

export default PreCancel;
